import {
  BASES_LIMITS,
  CHAT_CORNERS,
  checkChatText,
  defaultModeSettings,
  MODE_KIND_NAMES,
  MODE_PHRASE_LIMITS,
  MOGZO_PHRASES,
  type ModeKind,
  type ModeSettings,
  type ModeView,
  MULTIPLAYER_LIMITS,
  type SetupOrigin,
} from '@mogzo/shared';
import { Badge, Notice, SegmentedControl, Switch } from '@mogzo/ui';
import { type FormEvent, type ReactNode, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { boardIdFrom } from '../components/BoardForm';
import { CliTag, MadeFrom, touchedByCli } from '../components/MadeFrom';
import { useProject } from '../components/ProjectLayout';
import {
  Alert,
  Button,
  cardClass,
  ErrorNotice,
  fieldClass,
  Loading,
  TextLink,
} from '../components/ui';
import { ApiError, apiSend, type Project } from '../lib/api';
import { useApi } from '../lib/use-api';

type Env = 'test' | 'prod';

/** A mode as the Multiplayer tab lists it. */
interface ListedMode extends ModeView {
  origin: SetupOrigin;
  /** Attacks, for a bases mode. */
  matchesThisWeek: number;
  /** A realtime mode while realtime is off for live versions (M35). */
  testOnly: boolean;
}
interface BoardOption {
  id: string;
  name: string;
  sort: 'asc' | 'desc';
  format: 'number' | 'time';
  defined: boolean;
}

const KIND_HINTS: Record<ModeKind, string> = {
  turns: 'Players take turns on a shared state, live or hours apart. Board, card and word games.',
  bases: 'Players attack another player’s saved base while they’re away.',
  realtime: 'Everyone plays at once in a room. Shooters, racers, party games.',
};

const TURN_TIMES: [number, string][] = [
  [30, '30 seconds'],
  [60, '1 minute'],
  [120, '2 minutes'],
  [300, '5 minutes'],
  [3600, '1 hour'],
  [8 * 3600, '8 hours'],
  [86400, '1 day'],
  [3 * 86400, '3 days'],
  [7 * 86400, '7 days'],
];
const WIDEN: number[] = [5, 10, 15, 30, 60, 120];
const MESSAGE_SIZES = [64, 128, 256, 512, 1024, 2048];
const ROOM_MINUTES = [1, 3, 5, 10, 15, 30, 60];
const SHIELDS: [number, string][] = [
  [0, 'None'],
  [3600, '1 hour'],
  [4 * 3600, '4 hours'],
  [8 * 3600, '8 hours'],
  [12 * 3600, '12 hours'],
  [86400, '1 day'],
  [3 * 86400, '3 days'],
];
const ATTACK_TIMES: [number, string][] = [
  [30, '30 seconds'],
  [60, '1 minute'],
  [120, '2 minutes'],
  [180, '3 minutes'],
  [300, '5 minutes'],
  [600, '10 minutes'],
  [900, '15 minutes'],
];

/** "8 h shield", "no shield". */
function shieldText(seconds: number) {
  if (!seconds) return 'no shield';
  return seconds % 86400 === 0
    ? `${seconds / 86400} d shield`
    : seconds % 3600 === 0
      ? `${seconds / 3600} h shield`
      : `${Math.round(seconds / 60)} min shield`;
}

/** How a mode shows in the list: "2 players · quick match and invite", or a bases mode's. */
function summary(mode: ModeView) {
  return mode.kind === 'bases'
    ? `${mode.skill ? 'by skill' : 'anyone'} · ${shieldText(mode.shieldSeconds)}`
    : `${playersText(mode)} · ${meets(mode)}`;
}

function failure(error: unknown): string {
  return error instanceof ApiError ? error.message : 'Could not reach the API. Try again.';
}

function meets(mode: ModeView): string {
  if (mode.quickMatch && mode.invites) return 'quick match and invite';
  return mode.quickMatch ? 'quick match' : 'invite only';
}
function playersText(mode: Pick<ModeView, 'minPlayers' | 'maxPlayers'>): string {
  return mode.minPlayers === mode.maxPlayers
    ? `${mode.maxPlayers} players`
    : `${mode.minPlayers} to ${mode.maxPlayers} players`;
}

const labelClass = 'flex flex-col gap-1.5 text-[13px] font-semibold text-muted';
const hintClass = 'font-normal text-[13px] text-muted';

const CHAT_CHOICES = [
  ['off', 'Nothing', 'No chat in this mode.'],
  ['phrases', 'Quick phrases only', 'From a set: nothing typed.'],
  ['typed', 'Phrases and typing', 'Typed messages are checked by text safety.'],
] as const;

const CHAT_LOOK_CHOICES = [
  [
    'panel',
    'Panel',
    'Beside the game on a computer, a sheet over it on a phone. Suits slower games.',
  ],
  [
    'compact',
    'Over the game (compact)',
    'A few lines of white text on a dark see-through strip in a corner; typing opens a slim bar. Suits fast games.',
  ],
] as const;

const CORNER_NAMES: Record<ModeSettings['chatCorner'], string> = {
  'bottom-left': 'Bottom left',
  'top-left': 'Top left',
  'bottom-right': 'Bottom right',
  'top-right': 'Top right',
};

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-3.5 border-t border-line pt-5">
      <h3 className="text-base font-semibold">{title}</h3>
      {children}
    </div>
  );
}

function NumberSelect({
  label,
  value,
  options,
  onChange,
  hint,
}: {
  label: string;
  value: number;
  options: [number, string][];
  onChange: (value: number) => void;
  hint?: string;
}) {
  const known = options.some(([v]) => v === value);
  return (
    <label className={labelClass}>
      {label}
      <select
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
        className={fieldClass}
      >
        {!known && <option value={value}>{value}</option>}
        {options.map(([v, text]) => (
          <option key={v} value={v}>
            {text}
          </option>
        ))}
      </select>
      {hint && <span className={hintClass}>{hint}</span>}
    </label>
  );
}

/** Making, changing or (in live) looking at a mode: screens 1 and 2 of the approved mockup. */
function ModeForm({
  project,
  mode,
  boards,
  readOnly,
  realtimeOpen,
  onDone,
  onCancel,
}: {
  project: Project;
  mode: ListedMode | null;
  boards: BoardOption[];
  readOnly: boolean;
  /** Whether a super admin has opened realtime to live versions (M35). */
  realtimeOpen: boolean;
  onDone: (id: string | null) => void;
  onCancel: () => void;
}) {
  const [settings, setSettings] = useState<ModeSettings>(() => {
    if (!mode) return defaultModeSettings('turns', '');
    const {
      id: _id,
      origin: _origin,
      matchesThisWeek: _matches,
      testOnly: _testOnly,
      ...rest
    } = mode;
    // Settings a mode was made before (chat, M38b) start at their defaults.
    return { ...defaultModeSettings(rest.kind, rest.name), ...rest };
  });
  const [id, setId] = useState(mode?.id ?? '');
  const [idEdited, setIdEdited] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [phrase, setPhrase] = useState('');
  const [phraseProblem, setPhraseProblem] = useState<string | null>(null);
  const base = `/v1/dev/projects/${project.id}/modes`;
  const set = <K extends keyof ModeSettings>(key: K, value: ModeSettings[K]) => {
    setSaved(false);
    setSettings((current) => ({ ...current, [key]: value }));
  };
  const most =
    settings.kind === 'realtime'
      ? MULTIPLAYER_LIMITS.realtimePlayers
      : settings.kind === 'bases'
        ? 2
        : MULTIPLAYER_LIMITS.turnsPlayers;
  const counts = Array.from({ length: most - 1 }, (_, i) => i + 2).map(
    (n) => [n, String(n)] as [number, string],
  );
  const winBoards = boards.filter((b) => b.defined && b.sort === 'desc' && b.format === 'number');

  /** Adds a phrase of the game's own, checked as the API will check it. */
  function addPhrase() {
    const text = phrase.trim();
    if (!text) return;
    const taken = [...MOGZO_PHRASES, ...settings.phrases].some(
      (p) => p.toLowerCase() === text.toLowerCase(),
    );
    if (taken) return setPhraseProblem('That phrase is there already.');
    if (checkChatText(text).action !== 'allow') {
      return setPhraseProblem('That phrase has a word MOGZO doesn’t allow.');
    }
    set('phrases', [...settings.phrases, text]);
    setPhrase('');
    setPhraseProblem(null);
  }

  function pickKind(kind: ModeKind) {
    setSaved(false);
    setSettings((current) => {
      const cap =
        kind === 'realtime'
          ? MULTIPLAYER_LIMITS.realtimePlayers
          : kind === 'bases'
            ? 2
            : MULTIPLAYER_LIMITS.turnsPlayers;
      return {
        ...current,
        kind,
        minPlayers: kind === 'bases' ? 2 : Math.min(current.minPlayers, cap),
        maxPlayers: kind === 'bases' ? 2 : Math.min(current.maxPlayers, cap),
      };
    });
  }

  async function save(event: FormEvent) {
    event.preventDefault();
    const next = { ...settings, name: settings.name.trim() };
    setBusy(true);
    setError(null);
    try {
      if (mode) await apiSend('PUT', `${base}/${mode.id}`, next);
      else await apiSend('POST', base, { id, ...next });
      setSaved(true);
      onDone(mode?.id ?? id);
    } catch (failed) {
      setError(failure(failed));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!mode) return;
    setBusy(true);
    setError(null);
    try {
      await apiSend('DELETE', `${base}/${mode.id}`);
      onDone(null);
    } catch (failed) {
      setError(failure(failed));
      setBusy(false);
    }
  }

  return (
    <div className="flex min-w-0 flex-[999_1_560px] flex-col gap-5">
      <form
        onSubmit={save}
        aria-labelledby="mode-heading"
        className={`${cardClass} flex flex-col gap-5 p-6`}
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 id="mode-heading" className="flex items-center gap-2.5 text-xl font-semibold">
              {mode ? mode.name : 'New mode'}
              {mode?.testOnly && <Badge tone="warning">Test only</Badge>}
            </h2>
            <p className="mt-0.5 text-[13px] text-muted">
              {mode ? (
                <>
                  {MODE_KIND_NAMES[mode.kind]} · <span className="font-mono">{mode.id}</span> ·{' '}
                  {mode.kind === 'bases'
                    ? `${mode.matchesThisWeek} ${mode.matchesThisWeek === 1 ? 'attack' : 'attacks'} this week`
                    : `${mode.matchesThisWeek} ${mode.matchesThisWeek === 1 ? 'match' : 'matches'} this week`}
                </>
              ) : (
                'Your test community gets it right away. Live players get it when you publish a version.'
              )}
            </p>
            {mode && !readOnly && (
              <div className="mt-1.5">
                <MadeFrom origin={mode.origin} />
              </div>
            )}
          </div>
        </div>
        {error && <Alert tone="error">{error}</Alert>}
        {readOnly && (
          <p className="rounded-[6px] border border-dashed border-line px-4 py-3 text-sm text-muted">
            Live modes come from your last published version. Change them in Test, then publish a
            version.
          </p>
        )}

        <fieldset disabled={readOnly} className="m-0 flex min-w-0 flex-col gap-5 border-0 p-0">
          <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
            <legend className="mb-2 text-[13px] font-semibold text-muted">Kind</legend>
            <div className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-3">
              {(['turns', 'bases', 'realtime'] as const).map((kind) => (
                <label
                  key={kind}
                  className={`flex cursor-pointer flex-col gap-1.5 rounded-[10px] p-3.5 ${
                    settings.kind === kind ? 'border-2 border-ink' : 'border border-line'
                  }`}
                >
                  <span className="flex items-center gap-2 font-semibold">
                    <input
                      type="radio"
                      name="kind"
                      checked={settings.kind === kind}
                      onChange={() => pickKind(kind)}
                    />
                    {MODE_KIND_NAMES[kind]}
                    {kind === 'realtime' && !realtimeOpen && <Badge tone="warning">Testing</Badge>}
                  </span>
                  <span className="text-[13px] text-muted">{KIND_HINTS[kind]}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4">
            <label className={labelClass}>
              <span className="flex justify-between">
                Name
                <span className="text-xs font-medium">{settings.name.length} / 30</span>
              </span>
              <input
                value={settings.name}
                maxLength={30}
                onChange={(event) => {
                  set('name', event.target.value);
                  if (!mode && !idEdited) setId(boardIdFrom(event.target.value));
                }}
                className={fieldClass}
              />
              <span className={hintClass}>Players see it on MOGZO’s panels.</span>
            </label>
            <label className={labelClass}>
              ID
              <input
                value={id}
                readOnly={Boolean(mode)}
                onChange={(event) => {
                  setId(event.target.value);
                  setIdEdited(true);
                }}
                className={`${fieldClass} font-mono`}
              />
              <span className={hintClass}>
                {mode
                  ? 'Your game uses it. Fixed once made.'
                  : 'Made from the name. Fixed once made.'}
              </span>
            </label>
          </div>

          {settings.kind !== 'bases' && (
            <>
              <Section title="Players">
                <div className="grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-4">
                  <NumberSelect
                    label="At least"
                    value={settings.minPlayers}
                    options={counts}
                    onChange={(n) => set('minPlayers', n)}
                  />
                  <NumberSelect
                    label="At most"
                    value={settings.maxPlayers}
                    options={counts}
                    onChange={(n) => set('maxPlayers', n)}
                  />
                  <label className={labelClass}>
                    Teams
                    <select
                      value={settings.teams}
                      onChange={(event) => set('teams', Number(event.target.value) === 2 ? 2 : 0)}
                      className={fieldClass}
                    >
                      <option value={0}>No teams</option>
                      <option value={2}>2 teams</option>
                    </select>
                  </label>
                </div>
                <p className="text-[13px] text-muted">
                  Turns: 2 to {MULTIPLAYER_LIMITS.turnsPlayers} players. Realtime: up to{' '}
                  {MULTIPLAYER_LIMITS.realtimePlayers}. Bases: an attacker and a defender.
                </p>
              </Section>

              <Section title="How players meet">
                <label className="flex min-h-11 items-start gap-2.5">
                  <input
                    type="checkbox"
                    checked={settings.quickMatch}
                    onChange={(event) => set('quickMatch', event.target.checked)}
                    className="mt-1"
                  />
                  <span>
                    <b className="font-semibold">Quick match</b>
                    <span className="block text-[13px] text-muted">
                      MOGZO pairs players who pressed play at the same time.
                    </span>
                  </span>
                </label>
                <label className="flex min-h-11 items-start gap-2.5">
                  <input
                    type="checkbox"
                    checked={settings.invites}
                    onChange={(event) => set('invites', event.target.checked)}
                    className="mt-1"
                  />
                  <span>
                    <b className="font-semibold">Invite code</b>
                    <span className="block text-[13px] text-muted">
                      A 6-letter code and a link to share with a friend. It skips the queue and
                      lasts {MULTIPLAYER_LIMITS.inviteMinutes} minutes.
                    </span>
                  </span>
                </label>
                <div className="flex flex-col gap-3 rounded-[10px] bg-canvas p-4">
                  <Switch
                    label={<b className="font-semibold">Match by skill</b>}
                    checked={settings.skill}
                    onChange={(event) => set('skill', event.target.checked)}
                  />
                  <p className="text-[13px] text-muted">
                    Quick match pairs players of a similar skill in this mode, and widens the search
                    if nobody close is free. Players never see their rating.
                  </p>
                  {settings.skill && (
                    <div className="max-w-[260px]">
                      <NumberSelect
                        label="Widen the search after"
                        value={settings.widenAfter}
                        options={WIDEN.map((s) => [s, `${s} seconds`])}
                        onChange={(n) => set('widenAfter', n)}
                      />
                    </div>
                  )}
                </div>
              </Section>
            </>
          )}

          {settings.kind === 'turns' && (
            <Section title="Turns">
              <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4">
                <NumberSelect
                  label="Time for a turn"
                  value={settings.turnSeconds}
                  options={TURN_TIMES}
                  onChange={(n) => set('turnSeconds', n)}
                  hint="30 seconds to 7 days. A day or more lets players come back later."
                />
                <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
                  <legend className="mb-1.5 text-[13px] font-semibold text-muted">
                    When time runs out
                  </legend>
                  {(
                    [
                      ['skip', 'Skip their turn'],
                      ['forfeit', 'They lose the match'],
                    ] as const
                  ).map(([value, text]) => (
                    <label key={value} className="flex min-h-8 items-center gap-2.5">
                      <input
                        type="radio"
                        name="missed"
                        checked={settings.missedTurn === value}
                        onChange={() => set('missedTurn', value)}
                      />
                      {text}
                    </label>
                  ))}
                </fieldset>
              </div>
            </Section>
          )}

          {settings.kind === 'realtime' && (
            <Section title="The room">
              {!realtimeOpen && (
                <Notice tone="warning" title="This mode works only in testing for now">
                  Your test community and <span className="font-mono">mogzo dev</span> can play it.
                  Submitting a version that uses it is refused until realtime opens to players.
                </Notice>
              )}
              <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
                <legend className="mb-1.5 text-[13px] font-semibold text-muted">
                  Who keeps the game going
                </legend>
                {(
                  [
                    ['everyone', 'Every player', 'Each game sends its own moves to the others.'],
                    [
                      'host',
                      'One player hosts',
                      'One game runs the match and the others follow it. If the host leaves, another player takes over.',
                    ],
                  ] as const
                ).map(([value, text, hint]) => (
                  <label key={value} className="flex min-h-11 items-start gap-2.5">
                    <input
                      type="radio"
                      name="host"
                      checked={settings.host === value}
                      onChange={() => set('host', value)}
                      className="mt-1"
                    />
                    <span>
                      <b className="font-semibold">{text}</b>
                      <span className="block text-[13px] text-muted">{hint}</span>
                    </span>
                  </label>
                ))}
              </fieldset>
              <label className="flex min-h-11 items-start gap-2.5">
                <input
                  type="checkbox"
                  checked={settings.lateJoin}
                  onChange={(event) => set('lateJoin', event.target.checked)}
                  className="mt-1"
                />
                <span>
                  <b className="font-semibold">Players can join after the start</b>
                  <span className="block text-[13px] text-muted">
                    Free places are filled by quick match while the room plays.
                  </span>
                </span>
              </label>
              <div className="grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-4">
                <NumberSelect
                  label="Messages a second"
                  value={settings.messageRate}
                  options={Array.from({ length: 20 }, (_, i) => [i + 1, String(i + 1)])}
                  onChange={(n) => set('messageRate', n)}
                  hint="Per player, 1 to 20"
                />
                <NumberSelect
                  label="Message size"
                  value={settings.messageBytes}
                  options={MESSAGE_SIZES.map((b) => [
                    b,
                    b >= 1024 ? `${b / 1024} KB` : `${b} bytes`,
                  ])}
                  onChange={(n) => set('messageBytes', n)}
                  hint="Up to 2 KB"
                />
                <NumberSelect
                  label="Longest room"
                  value={settings.roomMinutes}
                  options={ROOM_MINUTES.map((m) => [m, m === 60 ? '1 hour' : `${m} minutes`])}
                  onChange={(n) => set('roomMinutes', n)}
                  hint="Up to 1 hour"
                />
              </div>
              <p className="text-[13px] text-muted">
                A player who drops out has 30 seconds to come back to their place. Empty rooms
                close.
              </p>
            </Section>
          )}

          {settings.kind === 'bases' && (
            <>
              <div className="flex flex-col gap-3 rounded-[10px] bg-canvas p-4">
                <Switch
                  label={<b className="font-semibold">Find opponents by skill</b>}
                  checked={settings.skill}
                  onChange={(event) => set('skill', event.target.checked)}
                />
                <p className="text-[13px] text-muted">
                  MOGZO picks a base whose owner is near the attacker’s skill, isn’t playing{' '}
                  {project.name} right now and isn’t shielded. With nobody close, it looks wider.
                  Players never see their rating.
                </p>
              </div>
              <Section title="Attacks">
                <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4">
                  <NumberSelect
                    label="Shield after an attack"
                    value={settings.shieldSeconds}
                    options={SHIELDS}
                    onChange={(n) => set('shieldSeconds', n)}
                    hint="None to 3 days. Nobody can attack that base again until it ends."
                  />
                  <NumberSelect
                    label="Time for an attack"
                    value={settings.attackSeconds}
                    options={ATTACK_TIMES}
                    onChange={(n) => set('attackSeconds', n)}
                    hint="30 seconds to 15 minutes. The base is kept for the attacker this long; a result after it is refused."
                  />
                  <label className={labelClass}>
                    Attacks an hour, per player
                    <input
                      type="number"
                      min={BASES_LIMITS.attacksPerHour.min}
                      max={BASES_LIMITS.attacksPerHour.max}
                      value={settings.attacksPerHour}
                      onChange={(event) => set('attacksPerHour', Number(event.target.value))}
                      className={fieldClass}
                    />
                    <span className={hintClass}>
                      1 to 60. More are refused until the hour is up.
                    </span>
                  </label>
                  <label className={labelClass}>
                    Most loot from one attack
                    <input
                      type="number"
                      min={0}
                      max={BASES_LIMITS.maxLoot}
                      value={settings.maxLoot ?? ''}
                      onChange={(event) =>
                        set(
                          'maxLoot',
                          event.target.value === '' ? null : Number(event.target.value),
                        )
                      }
                      className={fieldClass}
                    />
                    <span className={hintClass}>
                      Of each kind of loot. More is cut to it and flagged in the data browser.
                      Empty: no loot.
                    </span>
                  </label>
                </div>
                <p className="text-[13px] text-muted">
                  One attacker and one defender. Each player saves one base for this mode, up to{' '}
                  {BASES_LIMITS.baseBytes / 1024} KB of JSON; your game decides what’s in it.
                </p>
              </Section>
            </>
          )}

          <Section title="Results">
            <label className={`${labelClass} max-w-[420px]`}>
              Wins go to a leaderboard
              <select
                value={settings.leaderboard ?? ''}
                onChange={(event) => set('leaderboard', event.target.value || null)}
                className={fieldClass}
              >
                <option value="">None</option>
                {winBoards.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
              <span className={hintClass}>
                Each win adds 1 to the winner’s score. Boards where higher is better and scores are
                numbers.
              </span>
            </label>
          </Section>

          <Section title="Chat">
            <p className="-mt-1.5 text-[13px] text-muted">
              What players in a match of this mode can send each other. MOGZO draws the chat beside
              or over the game, checks every message and lets players report and block. Your game
              can open it with <span className="font-mono">mogzo.chat.open()</span> and show phrases
              over a player with <span className="font-mono">mogzo.chat.onPhrase()</span>.
            </p>
            <fieldset className="m-0 grid gap-2 border-0 p-0 sm:grid-cols-3">
              <legend className="mb-1.5 text-[13px] font-semibold text-muted">
                Players can send
              </legend>
              {CHAT_CHOICES.map(([value, text, hint]) => (
                <label
                  key={value}
                  className={`flex items-start gap-2.5 rounded-[8px] border px-3.5 py-3 ${settings.chat === value ? 'border-ink' : 'border-line'}`}
                >
                  <input
                    type="radio"
                    name="chat"
                    checked={settings.chat === value}
                    onChange={() => set('chat', value)}
                    className="mt-0.5"
                  />
                  <span>
                    <b className="block font-semibold">{text}</b>
                    <span className="text-[13px] text-muted">{hint}</span>
                  </span>
                </label>
              ))}
            </fieldset>
            {settings.chat !== 'off' && (
              <>
                <fieldset className="m-0 grid gap-2 border-0 p-0 sm:grid-cols-2">
                  <legend className="mb-1.5 text-[13px] font-semibold text-muted">
                    How it looks
                  </legend>
                  {CHAT_LOOK_CHOICES.map(([value, text, hint]) => (
                    <label
                      key={value}
                      className={`flex items-start gap-2.5 rounded-[8px] border px-3.5 py-3 ${settings.chatLook === value ? 'border-ink' : 'border-line'}`}
                    >
                      <input
                        type="radio"
                        name="chatLook"
                        checked={settings.chatLook === value}
                        onChange={() => set('chatLook', value)}
                        className="mt-0.5"
                      />
                      <span>
                        <b className="block font-semibold">{text}</b>
                        <span className="text-[13px] text-muted">{hint}</span>
                      </span>
                    </label>
                  ))}
                </fieldset>
                {settings.chatLook === 'compact' && (
                  <label className={`${labelClass} max-w-[280px]`}>
                    Where on the game
                    <select
                      value={settings.chatCorner}
                      onChange={(event) =>
                        set('chatCorner', event.target.value as ModeSettings['chatCorner'])
                      }
                      className={fieldClass}
                    >
                      {CHAT_CORNERS.map((corner) => (
                        <option key={corner} value={corner}>
                          {CORNER_NAMES[corner]}
                        </option>
                      ))}
                    </select>
                    <span className={hintClass}>
                      Keep it clear of your controls. While typing, it moves above the typing bar.
                    </span>
                  </label>
                )}
                <div className="flex flex-col gap-2">
                  <span className="text-[13px] font-semibold text-muted">Quick phrases</span>
                  <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0">
                    {MOGZO_PHRASES.map((p) => (
                      <li
                        key={p}
                        className="inline-flex h-7 items-center rounded-full border border-line bg-[var(--surface-sunken)] px-2.5 text-[13px]"
                      >
                        {p}
                      </li>
                    ))}
                    {settings.phrases.map((p) => (
                      <li
                        key={p}
                        className="inline-flex h-7 items-center gap-1 rounded-full border border-ink pr-1 pl-2.5 text-[13px] font-medium"
                      >
                        {p}
                        <button
                          type="button"
                          aria-label={`Remove ${p}`}
                          onClick={() =>
                            set(
                              'phrases',
                              settings.phrases.filter((other) => other !== p),
                            )
                          }
                          className="grid size-[22px] place-items-center rounded-full text-muted hover:bg-[var(--surface-sunken)]"
                        >
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                  <span className={hintClass}>
                    MOGZO’s, then up to {MODE_PHRASE_LIMITS.count} of your own, of{' '}
                    {MODE_PHRASE_LIMITS.length} letters each. Review sees them.
                  </span>
                  {settings.phrases.length < MODE_PHRASE_LIMITS.count && (
                    <div className="flex max-w-[420px] gap-2">
                      <label htmlFor="new-phrase" className="sr-only">
                        A phrase of your own
                      </label>
                      <input
                        id="new-phrase"
                        value={phrase}
                        maxLength={MODE_PHRASE_LIMITS.length}
                        placeholder="Catch me!"
                        onChange={(event) => {
                          setPhrase(event.target.value);
                          setPhraseProblem(null);
                        }}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                            addPhrase();
                          }
                        }}
                        className={`${fieldClass} min-w-0 flex-1`}
                      />
                      <Button variant="secondary" onClick={addPhrase} disabled={!phrase.trim()}>
                        Add
                      </Button>
                    </div>
                  )}
                  {phraseProblem && (
                    <span role="alert" className="text-[13px] text-danger">
                      {phraseProblem}
                    </span>
                  )}
                </div>
              </>
            )}
          </Section>
        </fieldset>

        {!readOnly && (
          <div className="flex flex-wrap items-center justify-end gap-2.5">
            <span role="status" className="mr-auto text-[13px] text-muted">
              {saved ? 'Saved. Your test community plays with it now.' : ''}
            </span>
            <Button variant="secondary" onClick={onCancel} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}>
              {busy ? 'Saving…' : mode ? 'Save' : 'Create mode'}
            </Button>
          </div>
        )}
      </form>

      {mode && !readOnly && (
        <section
          aria-labelledby="delete-mode-heading"
          className={`${cardClass} flex flex-col gap-3 border-danger px-6 py-5`}
        >
          <h2 id="delete-mode-heading" className="text-[15px] font-semibold">
            Delete this mode
          </h2>
          {confirmDelete ? (
            <div className="flex flex-col gap-2.5 rounded-[6px] bg-danger-soft px-4 py-3.5">
              <strong className="text-danger">Delete {mode.name} from test?</strong>
              <p className="text-muted">
                Its test matches end and its players’ ratings go. Calls for{' '}
                <span className="font-mono">{mode.id}</span> are refused from now on, so take it out
                of your game too. Live players keep it until you publish a version without it.
              </p>
              <div className="flex flex-wrap gap-2.5">
                <button
                  type="button"
                  onClick={remove}
                  disabled={busy}
                  className="mz-btn mz-btn-danger"
                >
                  Yes, delete it
                </button>
                <Button variant="secondary" onClick={() => setConfirmDelete(false)} disabled={busy}>
                  Keep it
                </Button>
              </div>
            </div>
          ) : (
            <div>
              <Button variant="danger" onClick={() => setConfirmDelete(true)}>
                Delete mode
              </Button>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

/** The Multiplayer tab (M31): a game's modes, test and live. */
export function ProjectMultiplayer() {
  const { project } = useProject();
  const [params, setParams] = useSearchParams();
  const env: Env = params.get('env') === 'prod' ? 'prod' : 'test';
  const [reload, setReload] = useState(0);
  const modes = useApi<{ modes: ListedMode[]; realtimeOpen: boolean }>(
    `/v1/dev/projects/${project.id}/modes?env=${env}`,
    reload,
  );
  const boards = useApi<{ boards: BoardOption[] }>(
    `/v1/dev/projects/${project.id}/leaderboards?env=test`,
    reload,
  );
  const [selected, setSelected] = useState<string | 'new' | null>(null);
  const list = modes.status === 'ready' ? modes.data.modes : [];
  const current = selected === 'new' ? null : (list.find((m) => m.id === selected) ?? null);

  // The first mode is open to start with.
  useEffect(() => {
    if (selected === null && list[0]) setSelected(list[0].id);
  }, [selected, list]);

  const go = (next: Env) => {
    setSelected(null);
    setParams(next === 'prod' ? { env: 'prod' } : {});
  };
  const shownId = current?.id ?? 'duel';

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0 flex-[1_1_420px]">
          <h2 className="text-[22px] leading-7 font-semibold">Multiplayer</h2>
          <p className="mt-1 max-w-[72ch] text-muted">
            Modes are the ways people play {project.name} together. Your game starts one with the
            SDK; MOGZO finds the players, keeps the turns and shows its own panels in your Game UI
            look.
          </p>
        </div>
        {env === 'test' && (
          <Button variant="brand" onClick={() => setSelected('new')}>
            New mode
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
        <SegmentedControl
          label="Which modes"
          value={env}
          onChange={go}
          items={[
            { id: 'test', label: 'Test' },
            { id: 'prod', label: 'Live' },
          ]}
        />
        <p className="m-0 flex-[1_1_320px] text-[13px] text-muted">
          {env === 'test'
            ? 'What your test community plays. Change anything here; live modes come from your last published version, checked in review.'
            : 'What players of your published version play. Change modes in Test, then publish a version.'}
        </p>
        <Badge tone={env === 'test' ? 'info' : 'success'}>
          {env === 'test' ? 'Test modes' : 'Live modes'}
        </Badge>
      </div>

      {modes.status === 'ready' && !modes.data.realtimeOpen && (
        <Notice tone="warning" title="Realtime is in testing">
          Realtime modes work in your test community and in{' '}
          <span className="font-mono">mogzo dev</span>. A version with one can’t be published yet;
          we’ll email you when realtime opens. Turn-based modes aren’t affected.
        </Notice>
      )}

      {modes.status === 'loading' && <Loading />}
      {modes.status === 'error' && <ErrorNotice error={modes.error} />}
      {modes.status === 'ready' && (
        <div className="flex flex-wrap items-start gap-5">
          <div className="flex max-w-[320px] min-w-0 flex-[1_1_280px] flex-col gap-4">
            <section
              aria-labelledby="modes-heading"
              className={`${cardClass} flex flex-col gap-2.5 p-4`}
            >
              <h3 id="modes-heading" className="mx-1 text-[15px] font-semibold">
                {list.length === 1 ? '1 mode' : `${list.length} modes`}
              </h3>
              {list.length === 0 ? (
                <p className="mx-1 text-sm text-muted">
                  {env === 'test'
                    ? 'No modes yet. Make one, or run mogzo modes create in your game’s folder.'
                    : 'Live has no modes yet. They come with the version you publish.'}
                </p>
              ) : (
                <ul className="flex flex-col gap-1">
                  {list.map((m) => (
                    <li key={m.id}>
                      <button
                        type="button"
                        aria-current={m.id === current?.id ? 'true' : undefined}
                        onClick={() => setSelected(m.id)}
                        className={`flex w-full flex-col gap-1 rounded-[8px] border-0 px-3 py-2.5 text-left ${
                          m.id === current?.id
                            ? 'bg-field shadow-[inset_2px_0_0_var(--color-ink)]'
                            : 'bg-transparent'
                        }`}
                      >
                        <span className="flex flex-wrap items-center gap-2 font-semibold">
                          {m.name}
                          {touchedByCli(m.origin) && <CliTag />}
                          {m.testOnly && <Badge tone="warning">Test only</Badge>}
                        </span>
                        <span className="text-[13px] text-muted">
                          {MODE_KIND_NAMES[m.kind]} · {summary(m)}
                        </span>
                        <span className="font-mono text-xs text-muted">{m.id}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section
              aria-labelledby="in-game-heading"
              className={`${cardClass} flex flex-col gap-2.5 p-4`}
            >
              <h3 id="in-game-heading" className="text-[15px] font-semibold">
                In your game
              </h3>
              {current?.kind === 'bases' ? (
                <>
                  <p className="text-[13px] text-muted">
                    Your game draws the base and the attack. MOGZO keeps the bases, picks opponents
                    and tells defenders.
                  </p>
                  <pre className="rounded-[6px] bg-field px-3 py-2.5 font-mono text-xs leading-[19px] whitespace-pre-wrap">
                    {`// The player's own base\nawait mogzo.bases.save('${shownId}', base);\n\n// Someone to attack, near their skill\nconst target = await mogzo.bases.findOpponent('${shownId}');\n// … play against target.base, then:\nawait mogzo.bases.reportAttack(target.id, { won: true, loot: { coins: 40 } });\n\n// Who attacked this player\nconst raids = await mogzo.bases.inbox('${shownId}');`}
                  </pre>
                </>
              ) : (
                <>
                  <p className="text-[13px] text-muted">
                    Start a mode from a button. MOGZO shows the panels and hands your game the
                    players.
                  </p>
                  <pre className="rounded-[6px] bg-field px-3 py-2.5 font-mono text-xs leading-[19px] whitespace-pre-wrap">
                    {`// Quick match\nconst match = await mogzo.multiplayer.find('${shownId}');\n\n// Play a friend\nawait mogzo.multiplayer.create('${shownId}');\n\n// A code from a link, or typed in\nawait mogzo.multiplayer.join();`}
                  </pre>
                </>
              )}
              <p className="text-[13px] text-muted">
                Or from the game’s folder: <span className="font-mono">mogzo modes list</span>
              </p>
              <TextLink to="/docs/multiplayer">Multiplayer in the docs</TextLink>
            </section>
          </div>

          {(selected === 'new' || current) && (
            <ModeForm
              key={`${env}:${selected}`}
              project={project}
              mode={current}
              boards={boards.status === 'ready' ? boards.data.boards : []}
              readOnly={env === 'prod'}
              realtimeOpen={modes.status === 'ready' && modes.data.realtimeOpen}
              onDone={(id) => {
                setSelected(id);
                setReload((n) => n + 1);
              }}
              onCancel={() => setSelected(list[0]?.id ?? null)}
            />
          )}
        </div>
      )}
    </div>
  );
}
